// src/pages/SiteEdit.js - AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import MapPicker from "../components/MapPicker";
import MapViewer from "../components/MapViewer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Couleurs de la charte graphique
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const SiteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    nom_site: "",
    addresse_site: "",
    longitude_site: "",
    latitude_site: "",
    statut_site: "1",
    id_client: ""
  });
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [mapKey, setMapKey] = useState(Date.now());
  const [showMapModal, setShowMapModal] = useState(false);
  const [geolocationStatus, setGeolocationStatus] = useState("idle"); // idle, loading, success, denied
  const [userLocation, setUserLocation] = useState(null); // {lat, lng, address}

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
        
        if (String(normalizedData.profile).trim() !== "3") {
          setError("Accès refusé. Seuls les gestionnaires peuvent modifier les sites.");
          setLoading(false);
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Fonction pour obtenir la géolocalisation de l'utilisateur
  const getUserGeolocation = () => {
    setGeolocationStatus("loading");
    
    if (!navigator.geolocation) {
      setGeolocationStatus("denied");
      setError("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({
          lat: latitude,
          lng: longitude
        });
        
        // Mettre à jour le formulaire avec les coordonnées
        setFormData(prev => ({
          ...prev,
          latitude_site: latitude.toString(),
          longitude_site: longitude.toString()
        }));
        
        // Essayer de récupérer l'adresse via l'API de géocodage inverse
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.display_name) {
              setUserLocation(prev => ({
                ...prev,
                address: data.display_name
              }));
              
              // Mettre à jour l'adresse dans le formulaire si vide
              if (!formData.addresse_site.trim()) {
                setFormData(prev => ({
                  ...prev,
                  addresse_site: data.display_name
                }));
              }
            }
          }
        } catch (error) {
          console.log("Erreur lors de la récupération de l'adresse:", error);
        }
        
        setGeolocationStatus("success");
        setMapKey(Date.now());
        
        setSuccessMsg("Position détectée automatiquement !");
        setTimeout(() => setSuccessMsg(""), 3000);
      },
      (error) => {
        console.error("Erreur de géolocalisation:", error);
        setGeolocationStatus("denied");
        
        // Si la géolocalisation est refusée, centrer sur la position existante du site
        if (formData.latitude_site && formData.longitude_site) {
          setMapKey(Date.now());
          setSuccessMsg("Utilisation des coordonnées du site");
          setTimeout(() => setSuccessMsg(""), 3000);
        }
      },
      options
    );
  };

  // Charger les données du site à modifier
  useEffect(() => {
    const fetchSiteData = async () => {
      if (!userInfo || !id) return;
      
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      try {
        // Charger le site
        const response = await axios.get(
          `${API_BASE_URL}/sites/get.php?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success && response.data.site) {
          const site = response.data.site;
          
          // Stocker les données originales
          setOriginalData(site);
          
          // Préparer les données pour le formulaire
          const formattedData = {
            nom_site: site.nom_site || "",
            addresse_site: site.addresse_site || "",
            longitude_site: site.longitude_site ? site.longitude_site.toString() : "",
            latitude_site: site.latitude_site ? site.latitude_site.toString() : "",
            statut_site: String(site.statut_site || "1"),
            id_client: String(site.id_client || "")
          };
          
          setFormData(formattedData);
          
          // Charger la liste des clients
          const clientsResponse = await axios.get(
            `${API_BASE_URL}/clients/list.php?limit=1000`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          
          if (clientsResponse.data?.success) {
            setClients(clientsResponse.data.data.clients || []);
          }
        } else {
          setError(response.data?.message || "Site non trouvé");
        }
      } catch (err) {
        console.error("Erreur:", err);
        
        if (err.response?.status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        } else if (err.response?.status === 404) {
          setError("Site non trouvé.");
          setTimeout(() => navigate("/sites"), 3000);
        } else if (!err.response) {
          setError("Serveur indisponible.");
        } else {
          setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (userInfo) {
      fetchSiteData();
    }
  }, [id, userInfo, navigate]);

  // Gestion des changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Gestion de la sélection sur la carte
  const handleMapLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      latitude_site: location.latitude.toString(),
      longitude_site: location.longitude.toString(),
      addresse_site: location.address || prev.addresse_site
    }));
    
    setMapKey(Date.now());
  };

  // Appliquer les coordonnées manuelles à la carte
  const handleApplyCoordinates = () => {
    if (!formData.latitude_site || !formData.longitude_site) {
      setError("Veuillez d'abord entrer des coordonnées valides");
      return;
    }
    
    const lat = parseFloat(formData.latitude_site);
    const lng = parseFloat(formData.longitude_site);
    
    if (isNaN(lat) || isNaN(lng)) {
      setError("Coordonnées invalides");
      return;
    }
    
    setMapKey(Date.now());
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = [];
    
    if (!formData.nom_site.trim()) {
      errors.push("Le nom du site est obligatoire");
    }
    
    if (!formData.addresse_site.trim()) {
      errors.push("L'adresse du site est obligatoire");
    }
    
    if (!formData.id_client) {
      errors.push("Un client doit être associé au site");
    }
    
    if (formData.longitude_site && isNaN(parseFloat(formData.longitude_site))) {
      errors.push("La longitude doit être un nombre valide");
    }
    
    if (formData.latitude_site && isNaN(parseFloat(formData.latitude_site))) {
      errors.push("La latitude doit être un nombre valide");
    }
    
    if (formData.latitude_site) {
      const lat = parseFloat(formData.latitude_site);
      if (lat < -90 || lat > 90) {
        errors.push("La latitude doit être entre -90 et 90");
      }
    }
    
    if (formData.longitude_site) {
      const lng = parseFloat(formData.longitude_site);
      if (lng < -180 || lng > 180) {
        errors.push("La longitude doit être entre -180 et 180");
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      
      const updateData = {
        id_site: parseInt(id),
        ...formData,
        id_client: parseInt(formData.id_client),
        statut_site: parseInt(formData.statut_site),
        longitude_site: formData.longitude_site ? parseFloat(formData.longitude_site) : 0,
        latitude_site: formData.latitude_site ? parseFloat(formData.latitude_site) : 0
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/sites/update.php`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Site modifié avec succès !");
        
        setTimeout(() => {
          navigate("/sites"); // Redirection vers la liste des sites
        }, 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la modification");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        // 🔍 Vérifier si c'est une erreur d'abonnement
        if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
        }
      } else if (err.response?.status === 409) {
        setError(err.response.data?.message || "Nom de site déjà utilisé pour ce client.");
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || "Données invalides");
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    if (originalData) {
      const formattedData = {
        nom_site: originalData.nom_site || "",
        addresse_site: originalData.addresse_site || "",
        longitude_site: originalData.longitude_site ? originalData.longitude_site.toString() : "",
        latitude_site: originalData.latitude_site ? originalData.latitude_site.toString() : "",
        statut_site: String(originalData.statut_site || "1"),
        id_client: String(originalData.id_client || "")
      };
      setFormData(formattedData);
      setMapKey(Date.now());
    }
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
    setGeolocationStatus("idle");
    setUserLocation(null);
  };

  // Annuler et retourner
  const handleCancel = () => {
    navigate(`/sites/details/${id}`);
  };

  // Afficher un message si l'utilisateur n'est pas gestionnaire
  if (!loading && userInfo && String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/sites">Sites</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted">Seuls les gestionnaires peuvent modifier les sites.</p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/sites")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le site</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/sites">Sites</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le site</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/sites">Sites</Link>
            </li>
            {originalData && (
              <li className="breadcrumb-item">
                <Link to={`/sites/details/${id}`}>
                  {originalData.nom_site}
                </Link>
              </li>
            )}
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modification</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-pencil-square me-2" style={{ color: COLORS.secondary }}></i>
              Modification du site
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                  <div className="flex-grow-1">
                    {successMsg}
                    {successMsg.includes("modifié") && (
                      <div className="mt-2">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Chargement...</span>
                        </div>
                        Redirection vers la liste des sites...
                      </div>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.warning
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Veuillez corriger les erreurs suivantes :</strong>
                    <ul className="mb-0 mt-2">
                      {validationErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}

            {/* Géolocalisation automatique */}
            <div className="card mb-4" style={{ 
              borderRadius: '8px',
              border: `1px solid ${COLORS.lightBg}`,
              boxShadow: 'none'
            }}>
              <div className="card-body">
                <h6 className="card-title mb-3" style={{ 
                  color: COLORS.primary,
                  fontWeight: '600'
                }}>
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                    width: '30px',
                    height: '30px',
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary
                  }}>
                    <i className="bi bi-geo"></i>
                  </div>
                  Position automatique
                </h6>
                
                <div className="d-flex align-items-center gap-3 mt-3">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={getUserGeolocation}
                    disabled={geolocationStatus === "loading" || saving}
                    style={{
                      color: geolocationStatus === "success" ? COLORS.success : COLORS.primary,
                      borderColor: geolocationStatus === "success" ? COLORS.success : COLORS.primary,
                      backgroundColor: geolocationStatus === "success" ? '#e8f5e9' : 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease',
                      flex: '0 0 auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving && geolocationStatus !== "loading") {
                        if (geolocationStatus !== "success") {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.color = 'white';
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (geolocationStatus !== "success") {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }
                    }}
                  >
                    {geolocationStatus === "loading" ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Détection...
                      </>
                    ) : geolocationStatus === "success" ? (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Position détectée
                      </>
                    ) : geolocationStatus === "denied" ? (
                      <>
                        <i className="bi bi-geo-alt me-2"></i>
                        Réessayer
                      </>
                    ) : (
                      <>
                        <i className="bi bi-geo-alt me-2"></i>
                        Détecter ma position
                      </>
                    )}
                  </button>
                  
                  {userLocation && (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <div className="flex-grow-1">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="badge" style={{ 
                            backgroundColor: COLORS.lightBg,
                            color: COLORS.darkText,
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.85rem'
                          }}>
                            <i className="bi bi-geo-alt me-1"></i>
                            Latitude: {userLocation.lat.toFixed(6)}
                          </span>
                          <span className="badge" style={{ 
                            backgroundColor: COLORS.lightBg,
                            color: COLORS.darkText,
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.85rem'
                          }}>
                            <i className="bi bi-geo-alt me-1"></i>
                            Longitude: {userLocation.lng.toFixed(6)}
                          </span>
                          {userLocation.address && (
                            <div className="text-truncate" style={{ 
                              maxWidth: '300px',
                              fontSize: '0.85rem',
                              color: COLORS.darkText
                            }} title={userLocation.address}>
                              <i className="bi bi-geo me-1"></i>
                              {userLocation.address}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <button
                        type="button"
                        className="btn btn-sm fw-semibold"
                        onClick={() => {
                          const textToCopy = userLocation.address 
                            ? `Latitude: ${userLocation.lat.toFixed(6)}\nLongitude: ${userLocation.lng.toFixed(6)}\nAdresse: ${userLocation.address}`
                            : `Latitude: ${userLocation.lat.toFixed(6)}\nLongitude: ${userLocation.lng.toFixed(6)}`;
                          navigator.clipboard.writeText(textToCopy);
                          setSuccessMsg("Coordonnées copiées dans le presse-papier !");
                          setTimeout(() => setSuccessMsg(""), 2000);
                        }}
                        style={{
                          color: COLORS.secondary,
                          borderColor: COLORS.secondary,
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          transition: 'all 0.3s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = COLORS.secondary;
                        }}
                      >
                        <i className="bi bi-clipboard me-1"></i>
                        Copier
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit}>
              {/* Informations principales */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-info-circle"></i>
                    </div>
                    Informations principales
                  </h6>
                  
                  {/* PREMIÈRE LIGNE - 3 CHAMPS ALIGNÉS */}
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-building me-1" style={{ color: COLORS.secondary }}></i>
                          Nom du site <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-pencil-square"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="nom_site"
                            value={formData.nom_site}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            placeholder="Ex: Siège social, Agence principale..."
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                        <div className="form-text" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Nom unique pour identifier le site.
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-people me-1" style={{ color: COLORS.secondary }}></i>
                          Client associé <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person-badge"></i>
                          </span>
                          <select
                            className="form-select"
                            name="id_client"
                            value={formData.id_client}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Sélectionner un client</option>
                            {clients.map(client => (
                              <option key={client.id_client} value={client.id_client}>
                                {client.nom_client} {client.prenom_client} 
                                {client.statut_client !== 1 && " (Inactif)"}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-geo-alt me-1" style={{ color: COLORS.secondary }}></i>
                          Adresse complète <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-geo-alt"></i>
                          </span>
                          <textarea
                            className="form-control"
                            name="addresse_site"
                            value={formData.addresse_site}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            rows="3"
                            placeholder="Adresse complète du site (rue, ville, pays)..."
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText,
                              minHeight: 'calc(2.5rem + 2px)',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* DEUXIÈME LIGNE - 3 CHAMPS GPS ALIGNÉS */}
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-geo me-1" style={{ color: COLORS.secondary }}></i>
                          Latitude
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-geo"></i>
                          </span>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            name="latitude_site"
                            value={formData.latitude_site}
                            onChange={handleChange}
                            disabled={saving}
                            placeholder="Ex: 13.5137"
                            min="-90"
                            max="90"
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0',
                              color: COLORS.darkText
                            }}
                          />
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.darkText
                          }}>°</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-geo me-1" style={{ color: COLORS.secondary }}></i>
                          Longitude
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-geo"></i>
                          </span>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            name="longitude_site"
                            value={formData.longitude_site}
                            onChange={handleChange}
                            disabled={saving}
                            placeholder="Ex: 2.1098"
                            min="-180"
                            max="180"
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0',
                              color: COLORS.darkText
                            }}
                          />
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.darkText
                          }}>°</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-geo-alt-fill me-1" style={{ color: COLORS.secondary }}></i>
                          Actions
                        </label>
                        <div className="d-grid">
                          <button
                            type="button"
                            className="btn fw-semibold"
                            onClick={handleApplyCoordinates}
                            disabled={saving || !formData.latitude_site || !formData.longitude_site}
                            title="Centrer la carte sur ces coordonnées"
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '10px 16px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!saving && formData.latitude_site && formData.longitude_site) {
                                e.target.style.backgroundColor = COLORS.primary;
                                e.target.style.color = 'white';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(11, 60, 93, 0.2)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = COLORS.primary;
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <i className="bi bi-geo-alt me-1"></i>
                            Appliquer aux coordonnées
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Carte interactive */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-geo-alt"></i>
                    </div>
                    Position géographique
                  </h6>
                  
                  {/* Carte avec key pour forcer le re-render */}
                  <div key={mapKey}>
                    <MapPicker 
                      onLocationSelect={handleMapLocationSelect}
                      initialPosition={
                        formData.latitude_site && formData.longitude_site
                          ? [
                              parseFloat(formData.latitude_site) || 13.5137,
                              parseFloat(formData.longitude_site) || 2.1098
                            ]
                          : [13.5137, 2.1098] // Niamey, Niger par défaut
                      }
                      initialZoom={5} // MODIFICATION ICI : Zoom à 5
                      manualLatitude={formData.latitude_site}
                      manualLongitude={formData.longitude_site}
                      showCoordinatesInputs={false}
                      showNigerRegions={false}
                      showInstructions={false}
                    />
                  </div>
                </div>
              </div>

              {/* Statut */}
              <div className="card mb-4" style={{ 
                borderRadius: '8px',
                border: `1px solid ${COLORS.lightBg}`,
                boxShadow: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-4" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-list-check"></i>
                    </div>
                    Statut
                  </h6>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          <i className="bi bi-toggle-on me-1" style={{ color: COLORS.secondary }}></i>
                          Statut du site <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-check-circle"></i>
                          </span>
                          <select
                            className="form-select"
                            name="statut_site"
                            value={formData.statut_site}
                            onChange={handleChange}
                            required
                            disabled={saving}
                            style={{ 
                              borderColor: '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="1">Actif</option>
                            <option value="0">Inactif</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="d-flex align-items-center h-100">
                        <div>
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                              width: '40px',
                              height: '40px',
                              backgroundColor: formData.statut_site === "1" ? '#e8f5e9' : '#ffebee',
                              color: formData.statut_site === "1" ? COLORS.success : COLORS.danger
                            }}>
                              <i className={`bi bi-${formData.statut_site === "1" ? 'check-circle' : 'x-circle'}`}></i>
                            </div>
                            <div>
                              <strong style={{ color: COLORS.darkText }}>
                                {formData.statut_site === "1" ? "Site actif" : "Site inactif"}
                              </strong>
                              <div className="text-muted small">
                                {formData.statut_site === "1" 
                                  ? "Le site est opérationnel et visible" 
                                  : "Le site est désactivé et masqué"}
                              </div>
                            </div>
                          </div>
                          
                          {formData.latitude_site && formData.longitude_site && (
                            <button
                              type="button"
                              className="btn btn-sm fw-semibold"
                              onClick={() => setShowMapModal(true)}
                              style={{
                                color: COLORS.primary,
                                borderColor: COLORS.primary,
                                backgroundColor: 'transparent',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = COLORS.primary;
                                e.target.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = COLORS.primary;
                              }}
                            >
                              <i className="bi bi-map me-1"></i>
                              Voir sur carte
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleReset}
                    disabled={saving}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="btn fw-semibold"
                  disabled={saving}
                  style={{
                    color: 'white',
                    backgroundColor: saving ? '#B0BEC5' : COLORS.primary,
                    borderColor: saving ? '#B0BEC5' : COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    opacity: saving ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Enregistrement en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Enregistrer les modifications
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Modal de visualisation de la carte */}
      {showMapModal && (
        <MapViewer
          show={showMapModal}
          onHide={() => setShowMapModal(false)}
          latitude={formData.latitude_site}
          longitude={formData.longitude_site}
          siteName={formData.nom_site || "Site en modification"}
          siteAddress={formData.addresse_site}
          siteStatus={formData.statut_site}
        />
      )}

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        .modal-backdrop {
          z-index: 1040;
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .modal {
          z-index: 1050;
        }
        
        .modal.show {
          display: block;
          background-color: rgba(0, 0, 0, 0.3);
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-md-4 {
            margin-bottom: 1rem;
          }
          
          .col-md-4 textarea.form-control {
            min-height: 120px;
          }
          
          .d-flex.align-items-center.gap-3 {
            flex-direction: column;
            align-items: stretch;
          }
          
          .d-flex.align-items-center.gap-3 .btn {
            width: 100%;
          }
          
          .d-flex.flex-wrap.align-items-center.gap-2 {
            justify-content: center;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SiteEdit;